import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPeople } from "redux/KPIs/peopleSlice";

import { Collapse } from "reactstrap";

import { currencyFormatter, getTeamColor, shortCurrencyFormatter } from "../../helpers";
import { NoTeamLabel } from "views/KPIs";
import ProgressBar from "components/UI/ProgressBar";
import ProfitPieChart from "./piecharts";
// import TeamBarChart from "./barCharts";
import GroupedTeamBarChart, { groupedMetricsValueExtractor } from "./groupedBarChart";

export const EmptyCellFiller = () => <><div>---</div></>;

export const People = () => {
    const people = useSelector((state) => state.KPIPeopleReducer.people);
    const quarter = useSelector((state) => state.KPIFilterReducer.quarter);
    const [teamMembersShown, setTeamMembersShown] = useState({});

    const dispatch = useDispatch();

    useEffect(() => {
        if (quarter) {
            dispatch(fetchPeople(quarter));
        }
    }, [quarter, dispatch]);

    // pie chart value extractors
    // profit
    const profitValueExtractor = (teams) => {
        // Team-level data
        const teamSeries = teams.map((team) => team.gross_profit);
        const teamLabels = teams.map((team) => team.name);
        const teamColors = teams.map((team) => getTeamColor(team.name));

        // Member-level data
        let memberSeries = [];
        let memberLabels = [];
        let memberColors = [];
        teams.forEach((team) => {
            const baseColor = getTeamColor(team.name);
            team.members.forEach((member) => {
                const profit = member.income - member.expense;
                memberSeries.push(profit);
                memberLabels.push(member.name);
                memberColors.push(baseColor);
            });
        });

        return {
            teamSeries,
            teamLabels,
            teamColors,
            memberSeries,
            memberLabels,
            memberColors,
        };
    };

    // revenue
    const revenueValueExtractor = (teams) => {
        // Team-level data
        const teamSeries = teams.map((team) => team.income);
        const teamLabels = teams.map((team) => team.name);
        const teamColors = teams.map((team) => getTeamColor(team.name));

        // Member-level data
        let memberSeries = [];
        let memberLabels = [];
        let memberColors = [];
        teams.forEach((team) => {
            const baseColor = getTeamColor(team.name);
            team.members.forEach((member) => {
                const profit = member.income;
                memberSeries.push(profit);
                memberLabels.push(member.name);
                memberColors.push(baseColor);
            });
        });

        return {
            teamSeries,
            teamLabels,
            teamColors,
            memberSeries,
            memberLabels,
            memberColors,
        };
    };

    // revenue
    const loadGoalValueExtractor = (teams) => {
        // Team-level data
        const teamSeries = teams.map((team) => team.invoice_count);
        const teamLabels = teams.map((team) => team.name);
        const teamColors = teams.map((team) => getTeamColor(team.name));

        // Member-level data
        let memberSeries = [];
        let memberLabels = [];
        let memberColors = [];
        teams.forEach((team) => {
            const baseColor = getTeamColor(team.name);
            team.members.forEach((member) => {
                const profit = member.invoice_count;
                memberSeries.push(profit);
                memberLabels.push(member.name);
                memberColors.push(baseColor);
            });
        });

        return {
            teamSeries,
            teamLabels,
            teamColors,
            memberSeries,
            memberLabels,
            memberColors,
        };
    };

    const DONUT_OPTIONS = {
        'gross_profit': {
            valueExtractor: profitValueExtractor,
            formatter: shortCurrencyFormatter,
        },
        'revenue': {
            valueExtractor: revenueValueExtractor,
            formatter: shortCurrencyFormatter,
        },
        'loads': {
            valueExtractor: loadGoalValueExtractor,
            formatter: (val) => `${val} Loads`,
        },
    }
    const [pieChartOption, setPieChartOptions] = useState(Object.keys(DONUT_OPTIONS)[0]);

    return (
        <>
            <div className="table-header kpi-section">
                <h1>
                    Expedite People and Teams
                </h1>
            </div>
            <table className="kpis-goals-table w-100">
                <thead>
                    <tr>
                        <th>Team</th>
                        <th>Loads Goal</th>
                        <th>Q Loads</th>
                        <th>Load Goal Completion</th>
                        <th>Revenue Goal</th>
                        <th>Revenue</th>
                        <th>Gross Profit Goal</th>
                        <th>Gross Profit</th>
                        <th>Gross Profit Goal Completion</th>
                    </tr>
                </thead>
                <tbody>
                    {people &&
                        people.map((team, index) => (
                            <React.Fragment key={team.name}>
                                <tr
                                    className={`kpi-goals-team-row balance-subtable ${team.name === NoTeamLabel ? 'company-row' : ''}`}
                                >
                                    <td>
                                        <div className="team-name">
                                            {`[${team.members.length}] ${team.name}`}
                                        </div>
                                    </td>
                                    <td>{team.invoice_count_goal}</td>
                                    <td>{team.invoice_count}</td>
                                    <td>
                                        {team.invoice_count_goal > 0
                                            ? `${Math.round(100 * parseFloat(team.invoice_count) / parseFloat(team.invoice_count_goal))}%`
                                            : 'N/A'}
                                    </td>
                                    <td>{currencyFormatter.format(team.revenue_goal)}</td>
                                    <td>{currencyFormatter.format(team.income)}</td>
                                    <td>{currencyFormatter.format(team.gross_profit_goal)}</td>
                                    <td>{currencyFormatter.format(team.gross_profit)}</td>
                                    <td>
                                        <div className="row">
                                            <div className={`col-3 ${team.name === "Company |---- Bench ----|" ? "company-row" : ""}`} style={{ alignContent: "center" }}>
                                                {team.gross_profit_goal > 0
                                                    ? `${Math.round(100 * parseFloat(team.gross_profit) / parseFloat(team.gross_profit_goal))}%`
                                                    : 'N/A'}
                                            </div>
                                            <div className="col-6 p-0 m-0" style={{ alignContent: "center" }}>
                                                <ProgressBar percentage={team.gross_profit_goal > 0 ? Math.round(100 * parseFloat(team.gross_profit) / parseFloat(team.gross_profit_goal)) : 0} />
                                            </div>
                                            <div className="actions-cell col-3">
                                                <button
                                                    className="btn-xs action-btn btn-collapse"
                                                    onClick={() => {
                                                        setTeamMembersShown({
                                                            ...teamMembersShown,
                                                            [team.name]: !teamMembersShown[team.name],
                                                        });
                                                    }}
                                                    title="Toggle Members"
                                                >
                                                    {teamMembersShown[team.name] ? (
                                                        <i className="fa fa-chevron-up"></i>
                                                    ) : (
                                                        <i className="fa fa-chevron-down"></i>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan="9" className="p-0 ignore">
                                        <Collapse isOpen={teamMembersShown[team.name]}>
                                            <table className="w-100">
                                                <tbody>
                                                    {team.members.map((person, index) => (
                                                        <tr className="balance-subtable" key={person.name}>
                                                            <td>{person.name}</td>
                                                            <td>{person.kpi_invoice_count_goal}</td>
                                                            <td>{person.invoice_count}</td>
                                                            <td>
                                                                {person.kpi_invoice_count_goal > 0
                                                                    ? `${Math.round(100 * parseFloat(person.invoice_count) / parseFloat(person.kpi_invoice_count_goal))}%`
                                                                    : 'N/A'}
                                                            </td>
                                                            <td><EmptyCellFiller /></td>
                                                            <td>{currencyFormatter.format(person.income)}</td>
                                                            <td><EmptyCellFiller /></td>
                                                            <td>
                                                                {`${currencyFormatter.format(Math.round(parseFloat(person.income) - parseFloat(person.expense)))}`}
                                                            </td>
                                                            <td><EmptyCellFiller /></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Collapse>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                </tbody>
            </table>
            <br />
            <div style={{ display: "flex", maxHeight: "2000px", overflow: "hidden", paddingTop: "8px" }}>
                <div className="flex-column mr-2" style={{ flex: 3 }}>
                    <div className="table-header kpi-section">
                        <h1>
                            <div className="mr-1">Overview:</div>
                            <select
                                className="table-title-select"
                                onChange={(e) => {
                                    setPieChartOptions(e.target.value);
                                }}
                            >
                                {Object.keys(DONUT_OPTIONS).map((key) => (
                                    <option key={key} value={key}>
                                        {key.replace('_', ' ').toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </h1>
                    </div>
                    <div className="kpi-pie-chart-container shadow">
                        {people && (
                            <>
                                <ProfitPieChart
                                    teams={people.filter((team) => team.name !== NoTeamLabel)}
                                    valueExtractor={DONUT_OPTIONS[pieChartOption].valueExtractor}
                                    defaultOutterFormatter={DONUT_OPTIONS[pieChartOption].formatter}
                                    label={
                                        "Total "
                                        + pieChartOption.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                                        + ":"
                                    }
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className="flex-column ml-2" style={{ flex: 4 }}>
                    <div className="table-header kpi-section">
                        <h1>
                            <div>Overview: Team Goals and Performance:</div>
                        </h1>
                    </div>
                    {people && (
                        <>
                            <div className="kpi-pie-chart-container shadow">
                                <GroupedTeamBarChart
                                    teams={people.filter((team) => team.name !== NoTeamLabel)}
                                    valueExtractor={groupedMetricsValueExtractor}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};